import { createSlice } from "@reduxjs/toolkit";
// api
import {
    combineOrderAPI, combineOrderRevertAPI, createTagListAPI, deleteOrderAPI, getAvailableCombineOrdersAPI, getOrdersAPI, getPackingSlipAPI, getPackingSlipsAPI,
    printLabelOrderAPI, sendTrackingEmailAPI, splitOrderAPI, splitOrderRevertAPI, updateOrderStatusAPI
} from "src/api";
// redux
import { batch } from "src/redux/store";
import { recursiveCheckTaskById, updateOrderRefreshStatus } from "src/redux/slices/task";
import { updateDisplayTaskPopover } from "src/redux/slices/other";
// utils
import apiSlice from "src/utils/api-slice";
import { openFile } from "src/utils/handle-task";
import { orderTagAddNew } from "src/utils/flatten-array";
import { trimObject } from "src/utils/format-object";
import { getOrderListBody } from "src/utils/get-request-data";

// ----------------------------------------------------------------------

const initialState = {
    isLoading: false,
    error: null,
    // ----------------------------------
    awaitingNumber: 0,
    //
    orderList: [],
    filter: {
        store_id: "all",
        carrier_code: "all",
        date_selection: "all",
        tag_ids: [],
        from_order_date: "",
        to_order_date: "",
        keyword: "",
        status: "all",
        page: 1,
        page_size: 25,
        total: 0
    },
    sidebarFilter: {
        to_city: "",
        item_name: "",
        item_sku: "",
        destination_type: "all",
        filter_combined: false,
        filter_split: false,
        has_order_note: null
    },
    //
    orderAwaitingList: [],
    awaitingFilter: {
        store_id: "all",
        carrier_code: "all",
        date_selection: "all",
        tag_ids: [],
        from_order_date: "",
        to_order_date: "",
        keyword: "",
        status: "awaiting",
        page: 1,
        page_size: 25,
        total: 0
    },
    awaitingSidebarFilter: {
        to_city: "",
        item_name: "",
        item_sku: "",
        destination_type: "all",
        filter_combined: false,
        filter_split: false,
        has_order_note: null
    },
    //
    orderShippedList: [],
    shippedFilter: {
        store_id: "all",
        carrier_code: "all",
        date_selection: "all",
        tag_ids: [],
        from_order_date: "",
        to_order_date: "",
        keyword: "",
        status: "shipped",
        page: 1,
        page_size: 25,
        total: 0
    },
    shippedSidebarFilter: {
        to_city: "",
        item_name: "",
        item_sku: "",
        destination_type: "all",
        filter_combined: false,
        filter_split: false,
        has_order_note: null
    },
    //
    orderCancelledList: [],
    cancelledFilter: {
        store_id: "all",
        carrier_code: "all",
        date_selection: "all",
        tag_ids: [],
        from_order_date: "",
        to_order_date: "",
        keyword: "",
        status: "cancel",
        page: 1,
        page_size: 25,
        total: 0
    },
    cancelledSidebarFilter: {
        to_city: "",
        item_name: "",
        item_sku: "",
        destination_type: "all",
        filter_combined: false,
        filter_split: false,
        has_order_note: null
    }
};

const slice = createSlice({
    name: "order",
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },

        stopLoading(state) {
            state.isLoading = false;
        },

        hasError(state, action) {
            state.isLoading = false;
            //
            state.error = action.payload;
        },

        clearError(state) {
            state.error = null;
        },

        // GET ORDER LIST
        getOrdersSuccess(state, action) {
            state.orderList = action.payload;
        },

        // UPDATE ORDER LIST FILTER
        updateOrderListFilterSuccess(state, action) {
            state.filter = {
                ...state.filter,
                ...action.payload
            };
        },

        // UPDATE ORDER SIDEBAR FILTER
        updateOrderListSidebarFilterSuccess(state, action) {
            state.sidebarFilter = {
                ...state.sidebarFilter,
                ...action.payload
            };
        },

        // GET ORDER AWAITING LIST
        getOrdersAwaitingSuccess(state, action) {
            state.orderAwaitingList = action.payload;
        },

        // UPDATE ORDER AWAITING LIST FILTER
        updateOrderAwaitingListFilterSuccess(state, action) {
            state.awaitingFilter = {
                ...state.awaitingFilter,
                ...action.payload
            };
        },

        // UPDATE ORDER AWAITING SIDEBAR FILTER
        updateOrderAwaitingListSidebarFilterSuccess(state, action) {
            state.awaitingSidebarFilter = {
                ...state.awaitingSidebarFilter,
                ...action.payload
            };
        },

        // GET ORDER SHIPPED LIST
        getOrdersShippedSuccess(state, action) {
            state.orderShippedList = action.payload;
        },

        // UPDATE ORDER SHIPPED LIST FILTER
        updateOrderShippedListFilterSuccess(state, action) {
            state.shippedFilter = {
                ...state.shippedFilter,
                ...action.payload
            };
        },

        // UPDATE ORDER SHIPPED SIDEBAR FILTER
        updateOrderShippedListSidebarFilterSuccess(state, action) {
            state.shippedSidebarFilter = {
                ...state.shippedSidebarFilter,
                ...action.payload
            };
        },

        // GET ORDER CANCELED LIST
        getOrdersCancelledSuccess(state, action) {
            state.orderCancelledList = action.payload;
        },

        // UPDATE ORDER CANCELED LIST FILTER
        updateOrderCancelledListFilterSuccess(state, action) {
            state.cancelledFilter = {
                ...state.cancelledFilter,
                ...action.payload
            };
        },

        // UPDATE ORDER CANCELED SIDEBAR FILTER
        updateOrderCancelledListSidebarFilterSuccess(state, action) {
            state.cancelledSidebarFilter = {
                ...state.cancelledSidebarFilter,
                ...action.payload
            };
        },

        getAwaitingNumberSuccess(state, action) {
            state.awaitingNumber = action.payload;
        }
    }
});

// Reducer
export default slice.reducer;

// Actions
export const {
    clearError
} = slice.actions;

// ----------------------------------------------------------------------

function getOrderAllList(filter) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await getOrdersAPI({
                ...trimObject(getOrderListBody(filter))([ "total" ])
            });

            if (status === 200 && data?.code === "ok") {
                batch(() => {
                    dispatch(slice.actions.getOrdersSuccess(data?.data?.orders || []));
                    dispatch(slice.actions.updateOrderListFilterSuccess({
                        total: data?.data?.count || 0
                    }));
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function updateOrderAllListFilter(filter) {
    return (dispatch) => batch(() => {
        dispatch(slice.actions.updateOrderListFilterSuccess({
            store_id: filter?.store_id ?? "all",
            carrier_code: filter?.carrier_code ?? "all",
            date_selection: filter?.date_selection ?? "all",
            tag_ids: filter?.tag_ids ?? [],
            from_order_date: filter?.from_order_date ?? "",
            to_order_date: filter?.to_order_date ?? "",
            tracking_number: filter?.tracking_number ?? "",
            order_number: filter?.order_number ?? "",
            keyword: filter?.keyword ?? "",
            page: filter?.page ?? 1,
            page_size: filter?.page_size ?? 25,
            total: filter?.total ?? 0
        }));

        dispatch(slice.actions.updateOrderListSidebarFilterSuccess({
            to_city: filter?.to_city ?? "",
            item_name: filter?.item_name ?? "",
            item_sku: filter?.item_sku ?? "",
            destination_type: filter?.destination_type ?? "all",
            filter_combined: filter?.filter_combined ?? false,
            filter_split: filter?.filter_split ?? false,
            has_order_note: filter?.has_order_note || null
        }));
    });
}

function resetAllFilter() {
    return (dispatch) => {
        const defaultFilter = {
            store_id: "all",
            carrier_code: "all",
            date_selection: "all",
            tag_ids: [],
            from_order_date: "",
            to_order_date: "",
            keyword: "",
            status: "all",
            page: 1,
            page_size: 25,
            total: 0
        };

        dispatch(slice.actions.updateOrderListFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

function resetAllSidebarFilter() {
    return (dispatch) => {
        const defaultFilter = {
            to_city: "",
            item_name: "",
            item_sku: "",
            destination_type: "all",
            filter_combined: false,
            filter_split: false,
            has_order_note: null
        };

        dispatch(slice.actions.updateOrderListSidebarFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

// ----------------------------------------------------------------------

function getOrderAwaitingList(filter) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await getOrdersAPI({
                ...trimObject(getOrderListBody(filter))([ "total" ])
            });

            if (status === 200 && data?.code === "ok") {
                batch(() => {
                    dispatch(slice.actions.getOrdersAwaitingSuccess(data?.data?.orders || []));
                    dispatch(slice.actions.updateOrderAwaitingListFilterSuccess({
                        total: data?.data?.count || 0
                    }));
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

function updateOrderAwaitingListFilter(filter) {
    return (dispatch) => batch(() => {
        dispatch(slice.actions.updateOrderAwaitingListFilterSuccess({
            store_id: filter?.store_id ?? "all",
            carrier_code: filter?.carrier_code ?? "all",
            date_selection: filter?.date_selection ?? "all",
            tag_ids: filter?.tag_ids ?? [],
            from_order_date: filter?.from_order_date ?? "",
            to_order_date: filter?.to_order_date ?? "",
            tracking_number: filter?.tracking_number ?? "",
            order_number: filter?.order_number ?? "",
            keyword: filter?.keyword ?? "",
            page: filter?.page ?? 1,
            page_size: filter?.page_size ?? 25,
            total: filter?.total ?? 0
        }));

        dispatch(slice.actions.updateOrderAwaitingListSidebarFilterSuccess({
            to_city: filter?.to_city ?? "",
            item_name: filter?.item_name ?? "",
            item_sku: filter?.item_sku ?? "",
            destination_type: filter?.destination_type ?? "all",
            filter_combined: filter?.filter_combined ?? false,
            filter_split: filter?.filter_split ?? false,
            has_order_note: filter?.has_order_note || null
        }));
    });
}

function resetAwaitingFilter() {
    return (dispatch) => {
        const defaultFilter = {
            store_id: "all",
            carrier_code: "all",
            date_selection: "all",
            tag_ids: [],
            from_order_date: "",
            to_order_date: "",
            keyword: "",
            status: "awaiting",
            page: 1,
            page_size: 25,
            total: 0
        };

        dispatch(slice.actions.updateOrderAwaitingListFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

function resetAwaitingSidebarFilter() {
    return (dispatch) => {
        const defaultFilter = {
            to_city: "",
            item_name: "",
            item_sku: "",
            destination_type: "all",
            filter_combined: false,
            filter_split: false,
            has_order_note: null
        };

        dispatch(slice.actions.updateOrderAwaitingListSidebarFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

// ----------------------------------------------------------------------

function getOrderShippedList(filter) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await getOrdersAPI({
                ...trimObject(getOrderListBody(filter))([ "total" ])
            });

            if (status === 200 && data?.code === "ok") {
                batch(() => {
                    dispatch(slice.actions.getOrdersShippedSuccess(data?.data?.orders || []));
                    dispatch(slice.actions.updateOrderShippedListFilterSuccess({
                        total: data?.data?.count || 0
                    }));
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

function updateOrderShippedListFilter(filter) {
    return (dispatch) => batch(() => {
        dispatch(slice.actions.updateOrderShippedListFilterSuccess({
            store_id: filter?.store_id ?? "all",
            carrier_code: filter?.carrier_code ?? "all",
            date_selection: filter?.date_selection ?? "all",
            tag_ids: filter?.tag_ids ?? [],
            from_order_date: filter?.from_order_date ?? "",
            to_order_date: filter?.to_order_date ?? "",
            tracking_number: filter?.tracking_number ?? "",
            order_number: filter?.order_number ?? "",
            keyword: filter?.keyword ?? "",
            page: filter?.page ?? 1,
            page_size: filter?.page_size ?? 25,
            total: filter?.total ?? 0
        }));

        dispatch(slice.actions.updateOrderShippedListSidebarFilterSuccess({
            to_city: filter?.to_city ?? "",
            item_name: filter?.item_name ?? "",
            item_sku: filter?.item_sku ?? "",
            destination_type: filter?.destination_type ?? "all",
            filter_combined: filter?.filter_combined ?? false,
            filter_split: filter?.filter_split ?? false,
            has_order_note: filter?.has_order_note || null
        }));
    });
}

function resetShippedFilter() {
    return (dispatch) => {
        const defaultFilter = {
            store_id: "all",
            carrier_code: "all",
            date_selection: "all",
            tag_ids: [],
            from_order_date: "",
            to_order_date: "",
            keyword: "",
            status: "shipped",
            page: 1,
            page_size: 25,
            total: 0
        };

        dispatch(slice.actions.updateOrderShippedListFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

function resetShippedSidebarFilter() {
    return (dispatch) => {
        const defaultFilter = {
            to_city: "",
            item_name: "",
            item_sku: "",
            destination_type: "all",
            filter_combined: false,
            filter_split: false,
            has_order_note: null
        };

        dispatch(slice.actions.updateOrderShippedListSidebarFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

// ----------------------------------------------------------------------

function getOrderCancelledList(filter) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await getOrdersAPI({
                ...trimObject(getOrderListBody(filter))([ "total" ])
            });

            if (status === 200 && data?.code === "ok") {
                batch(() => {
                    dispatch(slice.actions.getOrdersCancelledSuccess(data?.data?.orders || []));
                    dispatch(slice.actions.updateOrderCancelledListFilterSuccess({
                        total: data?.data?.count || 0
                    }));
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

function updateOrderCanceledListFilter(filter) {
    return (dispatch) => batch(() => {
        dispatch(slice.actions.updateOrderCancelledListFilterSuccess({
            store_id: filter?.store_id ?? "all",
            carrier_code: filter?.carrier_code ?? "all",
            date_selection: filter?.date_selection ?? "all",
            tag_ids: filter?.tag_ids ?? [],
            from_order_date: filter?.from_order_date ?? "",
            to_order_date: filter?.to_order_date ?? "",
            tracking_number: filter?.tracking_number ?? "",
            order_number: filter?.order_number ?? "",
            keyword: filter?.keyword ?? "",
            page: filter?.page ?? 1,
            page_size: filter?.page_size ?? 25,
            total: filter?.total ?? 0
        }));

        dispatch(slice.actions.updateOrderCancelledListSidebarFilterSuccess({
            to_city: filter?.to_city ?? "",
            item_name: filter?.item_name ?? "",
            item_sku: filter?.item_sku ?? "",
            destination_type: filter?.destination_type ?? "all",
            filter_combined: filter?.filter_combined ?? false,
            filter_split: filter?.filter_split ?? false,
            has_order_note: filter?.has_order_note || null
        }));
    });
}

function resetCancelledFilter() {
    return (dispatch) => {
        const defaultFilter = {
            store_id: "all",
            carrier_code: "all",
            date_selection: "all",
            tag_ids: [],
            from_order_date: "",
            to_order_date: "",
            keyword: "",
            status: "cancel",
            page: 1,
            page_size: 25,
            total: 0
        };

        dispatch(slice.actions.updateOrderCancelledListFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

function resetCancelledSidebarFilter() {
    return (dispatch) => {
        const defaultFilter = {
            to_city: "",
            item_name: "",
            item_sku: "",
            destination_type: "all",
            filter_combined: false,
            filter_split: false,
            has_order_note: null
        };

        dispatch(slice.actions.updateOrderCancelledListSidebarFilterSuccess({ ...defaultFilter }));

        return defaultFilter;
    };
}

// ----------------------------------------------------------------------

export function getList(filter) {
    return (dispatch, getState) => batch(() => {
        const status = getState().other.orderTab;

        try {
            let localFilter;

            switch (status) {
                case "awaiting":
                    localFilter = { ...( filter || { ...getState().order.awaitingFilter, ...getState().order.awaitingSidebarFilter } ) };

                    dispatch(updateOrderAwaitingListFilter(localFilter));
                    dispatch(getOrderAwaitingList(localFilter));
                    break;
                case "shipped":
                    localFilter = { ...( filter || { ...getState().order.shippedFilter, ...getState().order.shippedSidebarFilter } ) };

                    dispatch(updateOrderShippedListFilter(localFilter));
                    dispatch(getOrderShippedList(localFilter));
                    break;
                case "canceled":
                    localFilter = { ...( filter || { ...getState().order.cancelledFilter, ...getState().order.cancelledFilter } ) };

                    dispatch(updateOrderCanceledListFilter(localFilter));
                    dispatch(getOrderCancelledList(localFilter));
                    break;
                default:
                    localFilter = { ...( filter || { ...getState().order.filter, ...getState().order.sidebarFilter } ) };

                    dispatch(updateOrderAllListFilter(localFilter));
                    dispatch(getOrderAllList(localFilter));
                    break;
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(getAwaitingNumber());
        }
    });
}

// ----------------------------------

export function resetFilter() {
    return (dispatch, getState) => batch(async () => {
        try {
            const status = getState().other.orderTab;

            let tempFilter = {};
            let tempSidebarFilter = {};

            switch (status) {
                case "awaiting":
                    tempFilter = await dispatch(resetAwaitingFilter());
                    tempSidebarFilter = await dispatch(resetAwaitingSidebarFilter());
                    break;
                case "shipped":
                    tempFilter = await dispatch(resetShippedFilter());
                    tempSidebarFilter = await dispatch(resetShippedSidebarFilter());
                    break;
                case "canceled":
                    tempFilter = await dispatch(resetCancelledFilter());
                    tempSidebarFilter = await dispatch(resetCancelledSidebarFilter());
                    break;
                default:
                    tempFilter = await dispatch(resetAllFilter());
                    tempSidebarFilter = await dispatch(resetAllSidebarFilter());
                    break;
            }

            dispatch(getList({ ...tempFilter, ...tempSidebarFilter }));
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    });
}

// ----------------------------------------------------------------------

export function getAwaitingNumber() {
    return async (dispatch) => {
        try {
            const { status, data } = await getOrdersAPI({
                status: "awaiting",
                page_no: 1,
                page_size: 25
            });

            if (status === 200 && data?.code === "ok") {
                dispatch(slice.actions.getAwaitingNumberSuccess(data?.data?.count ?? 0));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

// ----------------------------------------------------------------------

export function changeOrderStatus(status, id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status: resultStatus, data } = await updateOrderStatusAPI(status, id);

            return resultStatus === 200 && data?.code === "ok";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        } finally {
            dispatch(slice.actions.stopLoading());
            //
            dispatch(getAwaitingNumber());
        }
    };
}

// ----------------------------------------------------------------------

export function deleteOrder(id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await deleteOrderAPI(id);

            return status === 200 && data?.code === "ok";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        } finally {
            dispatch(slice.actions.stopLoading());
            //
            dispatch(getAwaitingNumber());
        }
    };
}

// ----------------------------------------------------------------------

export function splitOrder(detail) {
    return async (dispatch) => {
        try {
            let result = null;

            if (detail?.items.length > 1) {
                result = await splitOrderAPI(detail);
            } else {
                result = await splitOrderRevertAPI(detail?.parent_order_id ?? "");
            }

            const { status, data } = result;

            return status === 200 && data?.code === "ok";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        } finally {
            dispatch(getAwaitingNumber());
        }
    };
}

// ----------------------------------------------------------------------

export function getCombineList() {
    return async (dispatch) => {
        try {
            const { status, data } = await getAvailableCombineOrdersAPI();

            return status === 200 ? ( data?.data || [] ) : null;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return null;
        }
    };
}

// ----------------------------------------------------------------------

export function combineOrder(list) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await combineOrderAPI({
                sub_order_ids: [ ...list ]
            });

            return ( status === 200 && data?.code === "ok" ) ? ( data?.data?.order_number ?? "" ) : "";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return "";
        } finally {
            dispatch(slice.actions.stopLoading());
            //
            dispatch(getAwaitingNumber());
        }
    };
}

// ----------------------------------------------------------------------

export function combineOrderRevert(id) {
    return async (dispatch) => {
        try {
            dispatch(slice.actions.startLoading());

            const { status, data } = await combineOrderRevertAPI(id);

            return status === 200 && data?.code === "ok";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        } finally {
            dispatch(slice.actions.stopLoading());
            //
            dispatch(getAwaitingNumber());
        }
    };
}

// ----------------------------------

export function updateTagList(request) {
    return async (dispatch) => {
        try {
            const { status } = await createTagListAPI({ ...request });

            if (status === 200) {
                dispatch(apiSlice.util.invalidateTags([ "TagList" ]));
            }

            return status === 200;
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return false;
        }
    };
}

export function replaceUpdateOrderTag(request) {
    return async (dispatch, getState) => {
        try {
            const status = getState().other.orderTab;

            if (status === "awaiting") {
                const list = getState().order.orderAwaitingList;
                const updatedList = list.map(order =>
                    request?.order_ids.includes(order.order_id)
                        ? {
                            ...order, tags: ( request?.tag_ids ?? [] ).map(item => ( {
                                id: item
                            } ))
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersAwaitingSuccess(updatedList));
            } else if (status === "shipped") {
                const list = getState().order.orderShippedList;
                const updatedList = list.map(order =>
                    request?.order_ids.includes(order.order_id)
                        ? {
                            ...order, tags: ( request?.tag_ids ?? [] ).map(item => ( {
                                id: item
                            } ))
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersShippedSuccess(updatedList));
            } else if (status === "canceled") {
                const list = getState().order.orderCancelledList;
                const updatedList = list.map(order =>
                    request?.order_ids.includes(order.order_id)
                        ? {
                            ...order, tags: ( request?.tag_ids ?? [] ).map(item => ( {
                                id: item
                            } ))
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersCancelledSuccess(updatedList));
            } else {
                const list = getState().order.orderList;
                const updatedList = list.map(order =>
                    request?.order_ids.includes(order.order_id)
                        ? {
                            ...order, tags: ( request?.tag_ids ?? [] ).map(item => ( {
                                id: item
                            } ))
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersSuccess(updatedList));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function addUpdateOrderTag(request) {
    return async (dispatch, getState) => {
        try {
            const status = getState().other.orderTab;

            if (status === "awaiting") {
                const list = getState().order.orderAwaitingList;
                const updatedList = orderTagAddNew(list, request);
                //
                dispatch(slice.actions.getOrdersAwaitingSuccess(updatedList));
            } else if (status === "shipped") {
                const list = getState().order.orderShippedList;
                const updatedList = orderTagAddNew(list, request);
                //
                dispatch(slice.actions.getOrdersShippedSuccess(updatedList));
            } else if (status === "canceled") {
                const list = getState().order.orderCancelledList;
                const updatedList = orderTagAddNew(list, request);
                //
                dispatch(slice.actions.getOrdersCancelledSuccess(updatedList));
            } else {
                const list = getState().order.orderList;
                const updatedList = orderTagAddNew(list, request);
                //
                dispatch(slice.actions.getOrdersSuccess(updatedList));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

export function removeUpdateOrderTag(request) {
    return async (dispatch, getState) => {
        try {
            const status = getState().other.orderTab;

            if (status === "awaiting") {
                const list = getState().order.orderAwaitingList;
                const updatedList = list.map(order =>
                    order.order_id === request?.order_id
                        ? {
                            ...order,
                            tags: order?.tags ? ( order?.tags ?? [] ).filter(item => !( request?.tag_ids ?? [] ).includes(item.id)) : null
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersAwaitingSuccess(updatedList));
            } else if (status === "shipped") {
                const list = getState().order.orderShippedList;
                const updatedList = list.map(order =>
                    order.order_id === request?.order_id
                        ? {
                            ...order,
                            tags: order?.tags ? ( order?.tags ?? [] ).filter(item => !( request?.tag_ids ?? [] ).includes(item.id)) : null
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersShippedSuccess(updatedList));
            } else if (status === "canceled") {
                const list = getState().order.orderCancelledList;
                const updatedList = list.map(order =>
                    order.order_id === request?.order_id
                        ? {
                            ...order,
                            tags: order?.tags ? ( order?.tags ?? [] ).filter(item => !( request?.tag_ids ?? [] ).includes(item.id)) : null
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersCancelledSuccess(updatedList));
            } else {
                const list = getState().order.orderList;
                const updatedList = list.map(order =>
                    order.order_id === request?.order_id
                        ? {
                            ...order,
                            tags: order?.tags ? ( order?.tags ?? [] ).filter(item => !( request?.tag_ids ?? [] ).includes(item.id)) : null
                        }
                        : order
                );
                dispatch(slice.actions.getOrdersSuccess(updatedList));
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        } finally {
            dispatch(slice.actions.stopLoading());
        }
    };
}

// ----------------------------------------------------------------------

export function downloadPackingSlip(id) {
    return async (dispatch, getState) => {
        try {
            const { packing_slip_note: note } = getState().automation.labelPackingSlip || {};

            const { status, data } = await getPackingSlipAPI({
                order_id: id,
                note
            });

            if (status === 200) {
                openFile(data);
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

export function downloadPackingSlips(request, snackbar) {
    return async (dispatch) => {
        try {
            const { status, data } = await getPackingSlipsAPI(request);

            if (status === 200) {
                batch(() => {
                    dispatch(recursiveCheckTaskById(data?.data, 0, snackbar));
                    //
                    dispatch(updateDisplayTaskPopover(true));
                    dispatch(updateOrderRefreshStatus(false));
                });
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

export function downloadLabel(id) {
    return async (dispatch) => {
        try {
            const { status, data } = await printLabelOrderAPI(id);

            if (status === 200) {
                openFile(data);
            }
        } catch (e) {
            dispatch(slice.actions.hasError(e));
        }
    };
}

export function sendTrackingEmail(id) {
    return async (dispatch) => {
        try {
            const { status, data } = await sendTrackingEmailAPI(id);

            return status === 200 && data?.code === "ok";
        } catch (e) {
            dispatch(slice.actions.hasError(e));
            //
            return null;
        }
    };
}
